import React from 'react';

import classnames from 'classnames';

import { CloseIcon } from '@cognite/cogs-icons-v11/system-icons/CloseIcon.tsx';
import { getStatusIcon } from '@cognite/cogs-icons-v11/utils/get-status-icon/index.ts';
import { IconWrapper } from '@cognite/cogs-utils-v11/components/icon-wrapper/icon-wrapper.tsx';
import { getThemeClassNames } from '@cognite/cogs-utils-v11/helpers/get-theme-class-names/get-theme-class-names.ts';

import { Body, Heading } from '../../tokens';
import { Button } from '../button';
import { Flex } from '../flex';

import type { InfoboxProps } from './types';

/**
 * Infobox is a component that is used to display information to the user.
 */
export const Infobox: React.ForwardRefExoticComponent<
  InfoboxProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, InfoboxProps>(
  (
    {
      className,
      inverted,
      title,
      icon = true,
      children,
      actions,
      status,
      onClose,
      compact = false,
      solid = false,
      ...otherProps
    },
    ref
  ) => {
    const InfoboxIcon = getStatusIcon(status || 'neutral');

    return (
      <div
        className={classnames(
          'cogs-v11',
          'cogs-lab-infobox-wrapper',
          ...getThemeClassNames(inverted),
          {
            'cogs-lab-infobox-wrapper-solid': solid,
          }
        )}
      >
        <Flex
          role="alert"
          gap={8}
          ref={ref}
          className={classnames(
            'cogs-v11',
            'cogs-lab-infobox',
            `cogs-lab-infobox-${status}`,
            compact && 'cogs-lab-infobox-compact',
            !title && 'cogs-lab-infobox-without-title',
            className
          )}
          {...otherProps}
        >
          {icon &&
            (InfoboxIcon ? (
              <IconWrapper
                className="cogs-v11 cogs-lab-infobox__icon-wrapper"
                size={16}
              >
                <InfoboxIcon />
              </IconWrapper>
            ) : null)}
          <Flex
            gap={8}
            className="cogs-v11 cogs-lab-infobox-content"
            flex={1}
            direction="column"
          >
            {title && <Heading level={5}>{title}</Heading>}
            {typeof children === 'string' ? (
              <Body className="cogs-v11 cogs-lab-infobox-body" size="medium">
                {children}
              </Body>
            ) : (
              children
            )}
            {actions && (
              <Flex gap={8}>
                {actions.map((action) => (
                  <Button {...action} />
                ))}
              </Flex>
            )}
          </Flex>
          {onClose && (
            <Button
              size="small"
              type="ghost"
              aria-label="Close Infobox"
              icon={<CloseIcon />}
              onClick={onClose}
              className="cogs-v11 cogs-lab-infobox-close-button"
            />
          )}
        </Flex>
      </div>
    );
  }
);
